import { observable, action } from "mobx";
import { findIndex } from "lodash";
import { ConsultantEditorStore } from "../../ConsultantEditorStore";
import {
  ExpertiseProfessionStore,
  ExpertiseProfession
} from "../ExpertiseProfessionStore";

export interface DoctorProfession extends ExpertiseProfession {
  areasOfExpertise: AreaOfExpertise[];
}

export interface AreaOfExpertise {
  name: string;
  qualified: boolean;
  interested: boolean;
  comment: string;
  yearFrom: string;
  open: boolean;
  specializations: Specialization[];
}

export interface Specialization {
  name: string;
  qualified: boolean;
  interested: boolean;
  comment: string;
}

export class DoctorStore extends ExpertiseProfessionStore<DoctorProfession> {
  @observable selectedAoE: string[];

  constructor(
    profession: string,
    public defaultAOE: string,
    storeIndex: number,
    public editorStore: ConsultantEditorStore
  ) {
    super(profession, storeIndex, editorStore);
  }

  get areasOfExpertise(): AreaOfExpertise[] {
    return this.currentExpertiseProfession?.areasOfExpertise ?? [];
  }

  get selectAoEList() {
    return this.editorStore
      .getAOEsOnProfession(this.profession)
      .filter((x) => x !== this.defaultAOE)
      .filter((x) => !this.areasOfExpertise?.any((y) => y.name === x));
  }

  @action setSelectedAoE = (val: string[]) => {
    this.selectedAoE = val;
  };

  @action addAoE = () => {
    this.selectedAoE.map((x) => {
      const i = findIndex(this.areasOfExpertise, (a) => a.name === x);
      if (i === -1) {
        this.editorStore.form.push(`professions.${this.storeIndex}.areasOfExpertise`, {
          name: x,
          qualified: true,
          interested: true,
          comment: "",
          yearFrom: "",
          open: true,
          specializations: this.editorStore
            .getSpecialization(x, this.profession)
            .map((y) => ({
              name: y,
              qualified: false,
              interested: false,
              comment: ""
            }))
        });
      }
    });
    this.setSelectedAoE(null);
  };

  @action removeAOEByIndex = (i: number) => {
    this.editorStore.form.removeByIndex(
      `professions.${this.storeIndex}.areasOfExpertise`,
      i
    );
  };

  @action toggleAOEByIndex = (i: number) => {
    this.areasOfExpertise[i].open = !this.areasOfExpertise[i].open;
    this.editorStore.form.setValue(
      `professions.${this.storeIndex}.areasOfExpertise`,
      this.areasOfExpertise
    );
  };

  initForm() {
    if (!this.currentExpertiseProfession) {
      this.editorStore.form.setValue(`professions.${this.storeIndex}`, {
        professionName: this.profession,
        systems: [],
        code: null,
        certifiedFrom: "",
        areasOfExpertise: this.getDefaultLakareAOE()
      });
    }
  }

  getDefaultLakareAOE() {
    if (this.profession === "Läkare") {
      const defaultAOE = [];

      this.editorStore.getAOEsOnProfession(this.profession).forEach((x) => {
        if (x === "Legitimerad läkare") {
          const specializations = this.editorStore
            .getSpecialization(x, this.profession)
            .map((y) => ({
              name: y,
              qualified: false,
              interested: false,
              comment: ""
            }));

          defaultAOE.push({
            name: x,
            comment: "",
            yearFrom: "",
            qualified: true,
            interested: true,
            specializations
          });
        }
      });

      return defaultAOE;
    }

    return [];
  }
}
